import React, { useState, useEffect, useRef, useCallback, lazy, Suspense } from 'react';
import SearchBar from './components/SearchBar';
import Header from './components/Header';
import SpotInfo from './components/SpotInfo';
import { about } from './about';

function formatForecastValue(value, decimals = 1) {
  const number = Number(value);
  return Number.isFinite(number) ? number.toFixed(decimals) : null;
}

function buildForecastOutlook(initialData) {
  const times = initialData?.hourly?.time;
  const heights = initialData?.hourly?.wave_height;
  const periods = initialData?.hourly?.wave_period;
  if (!Array.isArray(times) || !Array.isArray(heights) || !Array.isArray(periods)) return [];

  const today = new Date().toISOString().slice(0, 10);
  const days = new Map();
  for (let index = 0; index < times.length; index++) {
    const day = String(times[index]).split('T')[0];
    const height = Number(heights[index]);
    const period = Number(periods[index]);
    if (day < today || !Number.isFinite(height) || !Number.isFinite(period)) continue;

    const current = days.get(day) || { minHeight: height, maxHeight: height, minPeriod: period, maxPeriod: period };
    current.minHeight = Math.min(current.minHeight, height);
    current.maxHeight = Math.max(current.maxHeight, height);
    current.minPeriod = Math.min(current.minPeriod, period);
    current.maxPeriod = Math.max(current.maxPeriod, period);
    days.set(day, current);
    if (days.size === 3 && index < times.length - 1) {
      const nextDay = String(times[index + 1]).split('T')[0];
      if (nextDay !== day && days.has(nextDay)) break;
    }
  }

  return Array.from(days.entries()).slice(0, 3).map(([day, values]) => (
    `${day}: ${values.minHeight.toFixed(1)}-${values.maxHeight.toFixed(1)} m waves, ${values.minPeriod.toFixed(1)}-${values.maxPeriod.toFixed(1)} s period`
  ));
}

// Lazy-load heavy components to reduce initial bundle size
const MapComponent = lazy(() => import('./components/Map'));
const SurfInfo = lazy(() => import('./components/SurfInfo'));
const SurfEasy = lazy(() => import('./components/SurfEasy'));

function App({ initialData, initialTideData, windData, shoreReference, latitude, longitude, onLocationChange, onSearch, locationName }) {
  const [searchQuery, setSearchQuery] = useState('');
  
  // Initialize darkMode from localStorage synchronously on client, false on server
  const [darkMode, setDarkMode] = useState(() => {
    if (typeof window === 'undefined') return false;
    const stored = localStorage.getItem('darkMode');
    if (stored !== null) return JSON.parse(stored);
    return window.matchMedia('(prefers-color-scheme: dark)').matches;
  });
  
  // Keep SSR + first client render consistent: read localStorage only after mount.
  const [easyMode, setEasyMode] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [hoveredLocation, setHoveredLocation] = useState(null);
  const [hoverIndex, setHoverIndex] = useState(null);
  const [surfData, setSurfData] = useState(initialData?.surfData || []);
  const [tideData, setTideData] = useState(initialTideData || null);
  const [windDataState, setWindData] = useState(windData || []);
  const [showInfo, setShowInfo] = useState(false);
  const tableRef = useRef(null);
  const surfInfoRef = useRef(null);
  const [isClient, setIsClient] = useState(false);
  const [, setWaveEnergies] = useState([]);
  const [isSurfOverviewInteracting, setIsSurfOverviewInteracting] = useState(false);
  const [hasScrolledEasyToToday, setHasScrolledEasyToToday] = useState(false);
  const hasScrolledToInitialIndex = useRef(false);
  const hasInitializedHoverIndex = useRef(false);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    if (!isClient) return;
    try {
      const stored = localStorage.getItem('easyMode');
      setEasyMode(stored !== null ? JSON.parse(stored) : false);
    } catch (e) {
      setEasyMode(false);
    }
  }, [isClient]);

  useEffect(() => {
    const metaThemeColor = document.querySelector("meta[name=theme-color]");
    if (darkMode) {
      document.documentElement.classList.add('dark');
      metaThemeColor.setAttribute("content", "#18181b");
    } else {
      document.documentElement.classList.remove('dark');
      metaThemeColor.setAttribute("content", "#ffffff");
    }
  }, [darkMode]);

  useEffect(() => {
    setTideData(initialTideData || null);
  }, [initialTideData]);

  useEffect(() => {
    setSurfData(initialData?.surfData || []);
  }, [initialData]);

  useEffect(() => {
    setWindData(windData || []);
  }, [windData]);

  useEffect(() => {
    if (locationName && Number.isFinite(latitude) && Number.isFinite(longitude)) {
      setSelectedLocation({ latitude, longitude, text: locationName });
    } else {
      setSelectedLocation(null);
    }
  }, [locationName, latitude, longitude]);

  const handleDataLoaded = useCallback((surfInfoData) => {
    setWaveEnergies(surfInfoData);
    setSurfData(surfInfoData);
  }, []);

  // Listen for system dark mode changes after initial load
  useEffect(() => {
    if (isClient && localStorage.getItem('darkMode') === null) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      const handleChange = (e) => setDarkMode(e.matches);
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [isClient]);

  useEffect(() => {
    if (isClient && !easyMode && tableRef.current && hoverIndex !== null && isSurfOverviewInteracting) {
      let rafId = requestAnimationFrame(() => {
        const cell = tableRef.current.querySelector(`td:nth-child(${hoverIndex + 5})`);
        if (cell) {
          cell.scrollIntoView({
            behavior: 'auto',
            block: 'nearest',
            inline: 'center',
          });
        }
      });
      
      return () => cancelAnimationFrame(rafId);
    }
  }, [hoverIndex, isClient, isSurfOverviewInteracting, easyMode]);

  // Add this new effect for initial scroll on spot load (experminental might delete)
  useEffect(() => {
    if (isClient && !easyMode && tableRef.current && hoverIndex !== null && initialData && !isSurfOverviewInteracting && !hasScrolledToInitialIndex.current) {
      const scrollTimeout = setTimeout(() => {
        const cell = tableRef.current.querySelector(`td:nth-child(${hoverIndex + 5})`);
        if (cell) {
          cell.scrollIntoView({
            behavior: 'smooth',
            block: 'nearest',
            inline: 'center',
          });
          hasScrolledToInitialIndex.current = true;
        }
      }, 300); // Increased delay to ensure DOM is ready
      
      return () => clearTimeout(scrollTimeout);
    }
  }, [isClient, hoverIndex, initialData, latitude, longitude, isSurfOverviewInteracting, easyMode]); // Added hoverIndex and isClient to dependencies

  useEffect(() => {
    if (isClient) {
      localStorage.setItem('darkMode', JSON.stringify(darkMode));
    }
  }, [darkMode, isClient]);

  useEffect(() => {
    if (isClient) {
      localStorage.setItem('easyMode', JSON.stringify(easyMode));
    }
  }, [easyMode, isClient]);

  // Initialize hoverIndex to the current time slot whenever surf data loads or changes.
  // This fixes direct-URL loads where hoverIndex would otherwise stay null, leaving the
  // map dots unsynced and no card highlighted in SurfEasy until the user clicks something.
  useEffect(() => {
    if (hasInitializedHoverIndex.current || !initialData?.hourly?.time?.length) return;
    const times = initialData.hourly.time;
    const utcOffsetSeconds = initialData.utc_offset_seconds ?? 0;
    // times[i] are local-time strings without a timezone suffix (e.g. "2026-06-25T14:00").
    // Appending 'Z' treats them as fake-UTC, consistent with how SurfEasy/SurfInfo use them.
    // Adding the UTC offset to Date.now() gives the same fake-UTC representation of "now".
    const nowFakeUTC = Date.now() + utcOffsetSeconds * 1000;
    let best = 0;
    let bestDiff = Infinity;
    for (let i = 0; i < times.length; i++) {
      const diff = Math.abs(new Date(times[i] + 'Z').getTime() - nowFakeUTC);
      if (diff < bestDiff) { bestDiff = diff; best = i; }
    }
    setHoverIndex(best);
    hasInitializedHoverIndex.current = true;
  }, [initialData]);

  const handleSpotClick = useCallback((spot) => {
    const latitude = parseFloat(spot.latitude);
    const longitude = parseFloat(spot.longitude);

    if (isNaN(latitude) || isNaN(longitude)) {
      console.error('Invalid latitude or longitude:', spot);
      return;
    }

    setSearchQuery(spot.text || 'Selected Spot');
    setSelectedLocation({
      latitude: latitude,
      longitude: longitude,
      text: spot.text || 'Selected Spot'
    });
    
    onLocationChange(latitude, longitude, spot.text);
  }, [onLocationChange]);

  const toggleDarkMode = useCallback(() => {
    setDarkMode(prevMode => {
      const newMode = !prevMode;
      localStorage.setItem('darkMode', JSON.stringify(newMode));
      if (newMode) {
        document.documentElement.classList.add('dark');
      } else {
        document.documentElement.classList.remove('dark');
      }
      return newMode;
    });
  }, []);

  const toggleEasyMode = useCallback(() => {
    setEasyMode(prevMode => {
      const newMode = !prevMode;
      localStorage.setItem('easyMode', JSON.stringify(newMode));
      return newMode;
    });
  }, []);

  const toggleInfo = useCallback(() => {
    setShowInfo(prev => !prev);
  }, []);

  const handleCellClick = useCallback((index, fromSurfOverview = false) => {
    setHoverIndex(index);
    setIsSurfOverviewInteracting(fromSurfOverview);
  }, []);

  // Add a separate hover handler that doesn't trigger scrolling
  const handleCellHover = useCallback((index, isHovering) => {
    if (isHovering && index !== null) {
      setHoverIndex(index);
      // Don't set isSurfOverviewInteracting for hover - only for clicks
    } else if (!isHovering) {
      // Optionally reset hover index when not hovering
      // setHoverIndex(null);
    }
  }, []);

  // New function to handle time clicks from SurfEasy
  const handleTimeClick = useCallback((targetIndex) => {
    setHoverIndex(targetIndex);
    setIsSurfOverviewInteracting(false);
    
    // Wait for the next frame to ensure all components are rendered
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        // Now scroll to the bottom of the page
        const scrollHeight = Math.max(
          document.body.scrollHeight,
          document.documentElement.scrollHeight,
          document.body.offsetHeight,
          document.documentElement.offsetHeight,
          document.body.clientHeight,
          document.documentElement.clientHeight
        );
        
        window.scrollTo({
          top: scrollHeight + 100,
          behavior: 'smooth'
        });
        
        // Also scroll the table to the specific time
        if (isClient && tableRef.current) {
          setTimeout(() => {
            const cell = tableRef.current.querySelector(`td:nth-child(${targetIndex + 5})`);
            if (cell) {
              cell.scrollIntoView({
                behavior: 'smooth',
                block: 'nearest',
                inline: 'center',
              });
            }
          }, 300); // Wait for the page scroll to complete
        }
      });
    });
  }, [isClient]);

  const handleEasyTimeClick = useCallback((targetIndex) => {
    setHoverIndex(targetIndex);
    setIsSurfOverviewInteracting(false);
  }, []);

  const handleInitialEasyScroll = useCallback(() => {
    setHasScrolledEasyToToday(true);
  }, []);

  const handleMapClick = useCallback((longitude, latitude) => {
    setSearchQuery(`Custom Spot ${latitude.toFixed(2)} / ${longitude.toFixed(2)}`);
    setSelectedLocation({
      latitude,
      longitude,
      text: 'Custom Spot'
    });

    onLocationChange(latitude, longitude, 'Custom Spot');
  }, [onLocationChange]);

  const handleLocateUser = useCallback(() => {
    if (navigator.geolocation) {
      setSearchQuery('Getting your location...');
      setHoveredLocation(null); // Clear any hovered location
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const lat = position.coords.latitude;
          const lon = position.coords.longitude;
          setSearchQuery(`Current Location ${lat.toFixed(2)} / ${lon.toFixed(2)}`);
          setSelectedLocation({
            latitude: lat,
            longitude: lon,
            text: 'Current Location'
          });
          onLocationChange(lat, lon, 'Current Location');
        },
        (error) => {
          console.error('Geolocation error:', error);
          switch (error.code) {
            case error.PERMISSION_DENIED:
              setSearchQuery('Location access denied');
              break;
            case error.POSITION_UNAVAILABLE:
              setSearchQuery('Location unavailable');
              break;
            case error.TIMEOUT:
              setSearchQuery('Location request timed out');
              break;
            default:
              setSearchQuery('Error getting location');
          }
        },
        {
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 0
        }
      );
    } else {
      setSearchQuery('Location not supported');
    }
  }, [onLocationChange]);

  const handleSearch = useCallback(({ latitude, longitude, text }) => {
    // Update selectedLocation to ensure SpotInfo reflects the new selection
    setSelectedLocation({
      latitude: parseFloat(latitude),
      longitude: parseFloat(longitude),
      text: text || 'Selected Spot'
    });
    // Call the original onSearch prop (from Page.jsx) to handle data fetching and URL updates
    onSearch({ latitude, longitude, text });
  }, [onSearch]);

  const summaryWaveHeight = formatForecastValue(initialData?.hourly?.wave_height?.[0]);
  const summaryWavePeriod = formatForecastValue(initialData?.hourly?.wave_period?.[0]);
  const forecastOutlook = buildForecastOutlook(initialData);

  return (
    <div className="App h-dvh w-full overflow-x-hidden flex flex-col bg-white dark:bg-zinc-900 text-black dark:text-white">
      <div className="w-full px-4 lg:px-6 xl:px-8 flex-grow flex flex-col">
        <Header 
          toggleDarkMode={toggleDarkMode} 
          darkMode={darkMode} 
          toggleInfo={toggleInfo} 
          onLocateUser={handleLocateUser}
          toggleEasyMode={toggleEasyMode}
          easyMode={easyMode}
        />
        {showInfo && (
         <div className="bg-zinc-100 dark:bg-zinc-800 p-4 mb-4 rounded-sm">
          <div className="max-w-screen-lg mx-auto">
          <h1 className="text-xl font-semibold mb-4">About OceanOrb.org &nbsp; &nbsp; &nbsp; - &nbsp; &nbsp; &nbsp; Contact info@oceanorb.org</h1>
        
        <b className="block mb-2">{about.title1}</b>
        <p className="mb-4">{about.paragraph1}</p>
      
        <b className="block mb-2">{about.title2}</b>
        <p className="mb-4">{about.paragraph2}</p>
      
        <b className="block mb-2">{about.title3}</b>
        <p className="mb-4">{about.paragraph3}</p>
      
        <b className="block mb-2">{about.title4}</b>
        <p className="mb-4">{about.paragraph4}</p>
      
        <b className="block mb-2">{about.title5}</b>
        <p className="mb-6">{about.paragraph5}</p>
      
        <b className="block mb-2">{about.sources}</b>
        <ul className="mb-6">
          {about.dataSources.map((source, index) => (
            <li key={index} className="mb-1">{source}</li>
          ))}
        </ul>
      
        <b className="block mb-2">{about.legal1}</b>
        <b className="block mb-2">{about.legal2}</b>
        <p className="mb-4">{about.legal3}</p>
      
        <b className="block mb-2">{about.legal4}</b>
        <p className="mb-4">{about.legal5}</p>
      
        <b className="block mb-2">{about.legal6}</b>
        <p className="mb-4">{about.legal7}</p>
      
        <b className="block mb-2">{about.legal8}</b>
        <p className="mb-4">{about.legal9}</p>
      
        <b className="block mb-2">{about.legal10}</b>
        <p className="mb-4">{about.legal11}</p>
      
        <b className="block mb-2">{about.legal12}</b>
        <p>{about.legal13}</p>
   
          </div>
         </div>
        )}
        <SearchBar
          onSearch={handleSearch}  // Use handleSearch instead of onSearch
          setHoveredLocation={setHoveredLocation}
          searchQuery={searchQuery}
        />
        
        <SpotInfo
          selectedLocation={selectedLocation}
          locationName={locationName}
          latitude={latitude}
          longitude={longitude}
          timezone={initialData?.timezone}
          utcOffset={initialData?.utc_offset_seconds}
        />

        <div className="flex-grow flex">
          {isClient ? (
            <Suspense fallback={
              <div className="w-full h-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
                <div className="text-zinc-500">Loading map...</div>
              </div>
            }>
              <MapComponent 
                location={hoveredLocation || selectedLocation || (selectedLocation?.longitude && selectedLocation?.latitude ? selectedLocation : (longitude && latitude ? { longitude, latitude } : null))} 
                isDarkMode={darkMode}
                onMapClick={handleMapClick}
                surfData={surfData}
                windData={windDataState}
                hoverIndex={hoverIndex}
                onSpotClick={handleSpotClick}
              />
            </Suspense>
          ) : (
            <div className="w-full h-full bg-zinc-100 dark:bg-zinc-800 flex items-center justify-center">
              <div className="text-zinc-500">Loading map...</div>
            </div>
          )}
        </div>
        {isClient && (
          (selectedLocation && Number.isFinite(selectedLocation.latitude) && Number.isFinite(selectedLocation.longitude)) ||
          (Number.isFinite(latitude) && Number.isFinite(longitude))
        ) && (
          <div className="overflow-x-auto will-change-transform" ref={tableRef}>
            <div ref={surfInfoRef}>
              <Suspense fallback={<div className="bg-zinc-100 dark:bg-zinc-800 p-4 text-center">Loading...</div>}>
                <SurfInfo
                  latitude={selectedLocation?.latitude || latitude}
                  longitude={selectedLocation?.longitude || longitude}
                  onDataLoaded={handleDataLoaded}
                  hoverIndex={hoverIndex}
                  onCellClick={handleCellClick}
                  initialData={initialData}
                  initialTideData={tideData}
                  windData={windDataState}
                  isServer={false}
                  isSurfOverviewInteracting={isSurfOverviewInteracting}
                  shoreReference={shoreReference}
                  showTable={!easyMode}
                />
              </Suspense>
            </div>
            <div className={easyMode ? 'mode-enter' : 'hidden'}>
              <Suspense fallback={<div className="bg-zinc-100 dark:bg-zinc-800 p-4 text-center">Loading...</div>}>
                <SurfEasy
                  latitude={selectedLocation?.latitude || latitude}
                  longitude={selectedLocation?.longitude || longitude}
                  initialData={initialData}
                  initialTideData={tideData}
                  windData={windDataState}
                  onTimeClick={handleEasyTimeClick}
                  onHoverIndex={handleCellHover}
                  shoreReference={shoreReference}
                  hoverIndex={hoverIndex}
                  isSurfOverviewInteracting={isSurfOverviewInteracting}
                  shouldScrollToToday={easyMode && !hasScrolledEasyToToday}
                  onInitialTodayScroll={handleInitialEasyScroll}
                />
              </Suspense>
            </div>
          </div>  
        )}
        {locationName && initialData?.hourly && (
          <details className="mt-2 text-xs text-zinc-500 dark:text-zinc-400" aria-label={`${locationName} surf forecast summary`}>
            <summary className="cursor-pointer select-none">About this forecast</summary>
            <div className="pt-1">
              <h2 className="font-semibold">{locationName} surf forecast</h2>
              <p>
                Marine forecast data for {locationName} at {formatForecastValue(latitude, 4)}, {formatForecastValue(longitude, 4)}.
                {summaryWaveHeight && summaryWavePeriod
                  ? ` The current forecast starts with ${summaryWaveHeight} m waves at a ${summaryWavePeriod} s period.`
                  : ' Wave and period conditions are available in the detailed forecast above.'}
              </p>
              {forecastOutlook.length > 0 && (
                <p className="mt-1">Next days: {forecastOutlook.join(' | ')}</p>
              )}
            </div>
          </details>
        )}
        <div className='h-1.5'></div>
      </div>
    </div>
  );
}

export default App;
